import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  CourseStructureChapter,
  CourseStructureResponse,
  CourseStructureService,
  UnitScriptResponse,
} from '../../../services/course-structure.service';

/**
 * Read-only visual view of a course's PARSED structure (chapters in order,
 * per-chapter learning objectives, summaries, linked exercises) so a dozent
 * can verify what the guided-mode parser extracted. Keyed by the Moodle
 * canonical course uuid.
 */
@Component({
  // Renamed off the generic `app-course-structure-dialog` / class name to
  // avoid a selector + declaration collision with the Moodle-sync content
  // viewer of the same name in components/dialogs/. This one is the
  // guided-mode PARSED-structure viewer (chapters/objectives/mastery).
  selector: 'app-parsed-course-structure-dialog',
  templateUrl: './course-structure-dialog.component.html',
  styleUrl: './course-structure-dialog.component.scss',
})
export class ParsedCourseStructureDialogComponent implements OnInit, OnDestroy {
  @Input() course!: { id: string; fullname?: string; shortname?: string };
  /** Setzen, wenn `course.id` **unsere** Kurskennung ist und nicht die
   *  kanonische Moodle-Kennung. ILIAS-Platzierungen tragen sie direkt; ohne
   *  den Schalter würde der Dialog sie durch die Moodle-Umschlüsselung
   *  schicken und nichts finden. */
  @Input() byCourseId = false;

  loading = true;
  error = false;
  data: CourseStructureResponse | null = null;
  /** Struktur | Lernziel-Register (PRD mastery-objectives M2/M3). */
  view: 'structure' | 'register' = 'structure';
  viewOptions: { value: string; label: string }[] = [];
  /** Chapters for the register's chapter pick — built on load, not in a getter. */
  registerChapters: { id: string; title: string }[] = [];
  expanded = new Set<string>();

  /** Manual re-parse ("redo") state: button pressed → job enqueued → poll the
   *  parse-status until done/error, then reload the structure (the fresh
   *  result is committed lazily on that read). */
  reparsing = false;
  reparseMessage: string | null = null;
  reparseFailed = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  /** Live refresh while unit scripts are still compiling (see syncScriptPolling). */
  private scriptTimer: ReturnType<typeof setInterval> | null = null;
  private pollTicks = 0;
  /** 4s × 90 = 6 min — beyond that the job keeps running server-side, we just
   *  stop occupying the dialog. */
  private readonly maxPollTicks = 90;

  constructor(
    public ref: NbDialogRef<ParsedCourseStructureDialogComponent>,
    private svc: CourseStructureService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.viewOptions = [
      { value: 'structure', label: this.translate.instant('dialogs.courseStructure.views.structure') },
      { value: 'register', label: this.translate.instant('dialogs.courseStructure.views.register') },
    ];
    this.load(true);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopScriptPolling();
  }

  /** Refresh while a compile is in flight, stop as soon as it is not. Kept
   *  slow (20 s): the job takes minutes, and the dialog may stay open. */
  private syncScriptPolling(): void {
    const busy = this.scriptState === 'running' || this.scriptState === 'stalled';
    if (busy && this.scriptTimer === null) {
      this.scriptTimer = setInterval(() => this.load(), 20000);
    } else if (!busy) {
      this.stopScriptPolling();
    }
  }

  private stopScriptPolling(): void {
    if (this.scriptTimer !== null) {
      clearInterval(this.scriptTimer);
      this.scriptTimer = null;
    }
  }

  private load(autoExpandFirst = false): void {
    (this.byCourseId
      ? this.svc.getByCourse(this.course.id)
      : this.svc.getByCanonical(this.course.id)
    ).subscribe({
      next: (d) => {
        this.data = d;
        this.registerChapters = (d.chapters || []).map((c: any) => ({ id: c.id, title: c.title }));
        this.loading = false;
        this.error = false;
        if (autoExpandFirst && d.chapters?.length) this.expanded.add(d.chapters[0].id);
        // Compiling runs for minutes per unit. Keep the open dialog honest
        // instead of showing a frozen count until someone reopens it.
        this.syncScriptPolling();
        // Liegt noch nichts vor, kann trotzdem gerade analysiert werden — der
        // Lauf wird ja auch vom Inhaltsabgleich angestossen, nicht nur von
        // diesem Dialog. Ohne die Nachfrage stand hier "noch nichts da",
        // waehrend im Hintergrund genau das entstand.
        if (!this.hasStructure && !this.reparsing) this.detectRunningParse();
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  /** Läuft für diesen Kurs gerade eine Analyse? Dann den laufenden Zustand
   *  zeigen und mitverfolgen, statt den Leerzustand. Gilt für Moodle wie für
   *  ILIAS — beide stossen den Lauf aus dem Inhaltsabgleich an. */
  private detectRunningParse(): void {
    (this.byCourseId
      ? this.svc.getParseStatusByCourse(this.course.id)
      : this.svc.getParseStatusByCanonical(this.course.id)
    ).subscribe({
      next: (s) => {
        if (s.status === 'queued' || s.status === 'processing') {
          this.reparsing = true;
          this.reparseFailed = false;
          this.reparseMessage = this.translate.instant(
            'dialogs.courseStructure.running',
          );
          this.startPolling();
        }
      },
      error: () => {
        // Kein Status abrufbar — dann bleibt es beim Leerzustand.
      },
    });
  }

  reparse(): void {
    if (this.reparsing) return;
    this.reparsing = true;
    this.reparseFailed = false;
    this.reparseMessage = this.translate.instant('dialogs.courseStructure.reanalyseRunning');
    (this.byCourseId
      ? this.svc.reparseByCourse(this.course.id)
      : this.svc.reparseByCanonical(this.course.id)
    ).subscribe({
      next: (r) => {
        if (r.no_content || !r.enqueued) {
          this.reparsing = false;
          this.reparseFailed = true;
          this.reparseMessage = this.translate.instant(
            r.no_content
              ? 'dialogs.courseStructure.noSyncedContent'
              : 'dialogs.courseStructure.startFailed',
          );
          return;
        }
        this.startPolling();
      },
      error: () => {
        this.reparsing = false;
        this.reparseFailed = true;
        this.reparseMessage = this.translate.instant('dialogs.courseStructure.startFailed');
      },
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTicks = 0;
    this.pollTimer = setInterval(() => {
      this.pollTicks += 1;
      if (this.pollTicks > this.maxPollTicks) {
        this.stopPolling();
        this.reparsing = false;
        this.reparseFailed = false;
        this.reparseMessage = this.translate.instant(
          'dialogs.courseStructure.stillRunning',
        );
        return;
      }
      (this.byCourseId
        ? this.svc.getParseStatusByCourse(this.course.id)
        : this.svc.getParseStatusByCanonical(this.course.id)
      ).subscribe({
        next: (s) => {
          if (s.status === 'done') {
            this.stopPolling();
            this.reparsing = false;
            this.reparseFailed = false;
            this.reparseMessage = null;
            this.load();
          } else if (s.status === 'error') {
            this.stopPolling();
            this.reparsing = false;
            this.reparseFailed = true;
            this.reparseMessage =
              s.error_message
              || this.translate.instant('dialogs.courseStructure.analyseFailed');
          }
          // queued/processing → keep polling.
        },
        error: () => {
          // Transient — keep polling until the tick cap.
        },
      });
    }, 4000);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  get hasStructure(): boolean {
    return !!this.data?.structure && (this.data?.chapters?.length || 0) > 0;
  }

  toggle(id: string): void {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }
  isOpen(id: string): boolean {
    return this.expanded.has(id);
  }
  expandAll(): void {
    this.expanded = new Set((this.data?.chapters || []).map((c) => c.id));
  }
  collapseAll(): void {
    this.expanded = new Set();
  }
  get allExpanded(): boolean {
    const chapters = this.data?.chapters || [];
    return chapters.length > 0 && this.expanded.size >= chapters.length;
  }
  toggleAll(): void {
    if (this.allExpanded) this.collapseAll();
    else this.expandAll();
  }

  // ── Unit-script review ─────────────────────────────────────────────
  // The scripts are what the tutor actually teaches from. Without a way to
  // read them, a skewed condensation stays invisible; a note queues a rework
  // of that one script with the lecturer's correction.
  scriptOpenFor: { chapterId: string; unit: number; title: string } | null = null;
  scriptData: UnitScriptResponse | null = null;
  scriptLoading = false;
  scriptError = false;
  noteText = '';
  noteSaving = false;
  noteMessage: string | null = null;

  openScript(ch: CourseStructureChapter, unit: number, title: string, ev?: Event): void {
    ev?.stopPropagation();
    this.scriptOpenFor = { chapterId: ch.id, unit, title };
    this.scriptData = null;
    this.scriptError = false;
    this.scriptLoading = true;
    this.noteText = '';
    this.noteMessage = null;
    this.svc.getUnitScript(ch.id, unit).subscribe({
      next: (d) => {
        this.scriptData = d;
        this.scriptLoading = false;
      },
      error: () => {
        this.scriptError = true;
        this.scriptLoading = false;
      },
    });
  }

  closeScript(): void {
    this.scriptOpenFor = null;
    this.scriptData = null;
    this.noteText = '';
    this.noteMessage = null;
  }

  submitNote(): void {
    const target = this.scriptOpenFor;
    const note = (this.noteText || '').trim();
    if (!target || note.length < 5 || this.noteSaving) return;
    this.noteSaving = true;
    this.noteMessage = null;
    this.svc.addUnitScriptNote(target.chapterId, target.unit, note).subscribe({
      next: () => {
        this.noteSaving = false;
        this.noteText = '';
        this.noteMessage =
          'Hinweis gespeichert. Das Skript wird im Hintergrund überarbeitet.';
        this.openScriptRefresh();
      },
      error: () => {
        this.noteSaving = false;
        this.noteMessage = 'Hinweis konnte nicht gespeichert werden.';
      },
    });
  }

  private openScriptRefresh(): void {
    const t = this.scriptOpenFor;
    if (!t) return;
    this.svc.getUnitScript(t.chapterId, t.unit).subscribe({
      next: (d) => (this.scriptData = d),
      error: () => undefined,
    });
  }

  noteStatusLabel(status: string): string {
    switch (status) {
      case 'applied': return 'übernommen';
      case 'failed': return 'fehlgeschlagen';
      default: return 'in Arbeit';
    }
  }

  bloomStatus(bloom?: string | null): string {
    switch ((bloom || '').toLowerCase()) {
      case 'remember':
      case 'understand':
        return 'info';
      case 'apply':
      case 'analyze':
        return 'primary';
      case 'evaluate':
      case 'create':
        return 'success';
      default:
        return 'basic';
    }
  }

  get totalObjectives(): number {
    return (this.data?.chapters || []).reduce((n, c) => n + (c.objectives?.length || 0), 0);
  }
  get totalExercises(): number {
    return (this.data?.chapters || []).reduce((n, c) => n + (c.exercises?.length || 0), 0);
  }

  // ── Unit scripts (Stufe 2 of the Kurs-Didaktik pipeline) ────────────
  // A chapter's teaching units are its sections, or ONE unit (number 0)
  // when it has none. Script status per unit: done | compiling | failed |
  // missing (not compiled yet / no usable source text).

  unitCount(ch: CourseStructureChapter): number {
    return ch.sections?.length ? ch.sections.length : 1;
  }

  scriptStatus(ch: CourseStructureChapter, unitNumber: number): 'done' | 'compiling' | 'failed' | 'missing' {
    const rows = (ch.unit_scripts || []).filter((s) => s.unit_number === unitNumber);
    if (rows.some((r) => r.status === 'done')) return 'done';
    if (rows.some((r) => r.status === 'compiling')) return 'compiling';
    if (rows.some((r) => r.status === 'failed')) return 'failed';
    return 'missing';
  }

  scriptLabel(status: string): string {
    switch (status) {
      case 'done': return 'Skript kompiliert';
      case 'compiling': return 'Skript wird kompiliert…';
      case 'failed': return 'Skript fehlgeschlagen';
      default: return 'Skript fehlt noch';
    }
  }

  /** What the script counter means right now. The count alone is ambiguous:
   *  0/32 reads the same whether nothing ever started, a compile is running
   *  (minutes of LLM work per unit — the normal case after a parse), or the
   *  job died. */
  get scriptState(): 'complete' | 'running' | 'stalled' | 'failed' | 'none' | 'partial' {
    const job = this.data?.script_job;
    const total = this.totalUnits;
    const done = this.totalScriptsDone;
    if (total > 0 && done === total) return 'complete';
    if (job) {
      if (job.stale) return 'stalled';
      if (job.status === 'queued' || job.status === 'running') return 'running';
      if (job.status === 'failed' || job.status === 'dead') return 'failed';
    }
    return done > 0 ? 'partial' : 'none';
  }

  /** i18n key + params for the state line. Translated in the template — the
   *  portal keeps every user-visible string in de.json/en.json. */
  get scriptStateKey(): string {
    const base = 'dialogs.courseStructure.scripts.';
    if (this.scriptState === 'failed' && this.data?.script_job?.last_error) {
      return base + 'failedWithError';
    }
    return base + (this.scriptState === 'none' ? 'none' : this.scriptState);
  }

  get scriptStateParams(): Record<string, string> {
    return { error: this.data?.script_job?.last_error || '' };
  }

  chapterScriptsDone(ch: CourseStructureChapter): number {
    let n = 0;
    for (let i = 0; i < this.unitCount(ch); i++) if (this.scriptStatus(ch, i) === 'done') n++;
    return n;
  }

  get totalUnits(): number {
    return (this.data?.chapters || []).reduce((n, c) => n + this.unitCount(c), 0);
  }

  get totalScriptsDone(): number {
    return (this.data?.chapters || []).reduce((n, c) => n + this.chapterScriptsDone(c), 0);
  }
}
